/**
 * v4 Today - minimal launcher.
 *
 * Just three things on the screen: the firm logo, three big circle shortcuts,
 * and a quiet "Powered by RoniDoc" footer. All other dashboard data lives one
 * tap deeper.
 */

import { useState, useEffect, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Settings as SettingsIcon, Bell, X, MessageSquareText, FileCheck2, Newspaper, ChevronRight,
  Sparkles, Briefcase, FolderOpen, Mic, Play, Pause, ArrowLeft, Reply, Send,
  Inbox as InboxIcon, CalendarClock, MessageCircle, FileText, CheckCircle2, UserCheck, Loader2,
} from 'lucide-react';
import { useV4 } from './i18n';
import { AnimatedRoniDocLogo } from './AnimatedRoniDocLogo';
import { cn } from '@/lib/utils';
import { listMessages, listTimeline, listFilesTimeline, getFileDownloadUrl, markMessagesRead, sendMessage, uploadFile, ClientMessage, TimelineEvent, FileTimelineItem } from '@/services/api/client-auth';
import { Paperclip, Image as ImageIcon } from 'lucide-react';
import { VoiceRecorder } from './VoiceRecorder';
import { clearAppBadge } from '@/lib/appBadge';
import { toast } from 'sonner';

interface InboxMsg {
  id: string;
  type: 'personal' | 'update' | 'news' | 'voice';
  title: string;
  preview: string;
  time: string;
  unread: boolean;
  /** for voice messages only - duration in seconds */
  voiceSec?: number;
  attachmentUrl?: string | null;
  attachmentName?: string | null;
  attachmentContentType?: string | null;
  /** 'inbound' = client sent to firm; 'outbound' = firm sent to client */
  direction: 'inbound' | 'outbound';
  senderName?: string | null;
  /** raw ISO for proper sorting + grouping by day */
  createdAt: string;
}

function relativeTime(iso: string): string {
  const d = new Date(iso).getTime();
  const diff = Math.round((Date.now() - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function serverMsgToInbox(m: ClientMessage): InboxMsg {
  return {
    id: m.id,
    type: m.attachmentContentType?.startsWith('audio/') ? 'voice' : (m.attachmentFileId ? 'update' : 'personal'),
    title: m.senderName ? `${m.senderName}` : (m.direction === 'inbound' ? 'You sent a message' : 'From your accountant'),
    preview: m.body || (m.attachmentName ? `📎 ${m.attachmentName}` : ''),
    time: relativeTime(m.createdAt),
    unread: m.direction === 'outbound' && !m.readAt,
    attachmentUrl: m.attachmentUrl,
    attachmentName: m.attachmentName,
    attachmentContentType: m.attachmentContentType,
    direction: m.direction,
    senderName: m.senderName,
    createdAt: m.createdAt,
  };
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
}

function timeOfDay(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function HomePageV4() {
  const { t, dir } = useV4();
  const initials = t('firm_initials') || 'UAG';
  const firmName = t('firm_name');
  const [showInbox, setShowInbox] = useState(false);
  const qc = useQueryClient();
  const { data: messagesData } = useQuery({
    queryKey: ['client-messages'],
    queryFn: () => listMessages(50),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const { data: timelineData } = useQuery({
    queryKey: ['client-timeline'],
    queryFn: () => listTimeline(50),
    staleTime: 30_000,
  });
  const { data: filesTimelineData } = useQuery({
    queryKey: ['client-files-timeline'],
    queryFn: () => listFilesTimeline(100),
    staleTime: 30_000,
  });
  const inbox: InboxMsg[] = (messagesData?.messages || []).map(serverMsgToInbox);
  const timeline: TimelineEvent[] = timelineData || [];
  const filesTimeline: FileTimelineItem[] = filesTimelineData || [];
  const unread = messagesData?.unreadCount ?? 0;

  const openInbox = () => setShowInbox(true);
  const markAllRead = async () => {
    try {
      await markMessagesRead();
      clearAppBadge();
      qc.invalidateQueries({ queryKey: ['client-messages'] });
      qc.invalidateQueries({ queryKey: ['client-unread-badge'] });
    } catch { /* ignore */ }
  };
  // Per-message read marking is handled by markAllRead on the next refetch; the
  // local UI optimistic update from the original mock is no longer needed.
  const readOne = (_id: string) => { /* no-op; mark-all-read on open */ };

  return (
    <div className="relative flex min-h-[calc(100vh-80px)] flex-col items-center justify-between gap-12 pt-12 pb-6">
      {/* Messages bell in the top-LEFT corner */}
      <button
        onClick={openInbox}
        className="absolute top-3 left-3 flex h-10 w-10 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors rtl:left-auto rtl:right-3"
        aria-label="Messages"
      >
        <Bell className="h-5 w-5" strokeWidth={1.5} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[var(--v4-accent)] px-1 text-[10px] font-bold leading-none text-white ring-2 ring-background">
            {unread}
          </span>
        )}
      </button>

      {/* Quiet settings gear in the top-right corner */}
      <Link
        to="/client/settings"
        className="absolute top-3 right-3 flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors rtl:right-auto rtl:left-3"
        aria-label="Settings"
      >
        <SettingsIcon className="h-5 w-5" strokeWidth={1.5} />
      </Link>

      {/* Firm logo block */}
      <div className="flex flex-col items-center text-center">
        <h1 className="text-[clamp(3.5rem,16vw,5.5rem)] font-light leading-none tracking-tight text-foreground">
          {/* Highlight one letter in the accent color (the LAST one - keeps the
              brand letter dominant) */}
          {[...initials].map((ch, i) => (
            <span
              key={i}
              className={i === Math.floor(initials.length / 2) ? 'text-[var(--v4-accent)]' : ''}
            >
              {ch}
            </span>
          ))}
        </h1>
        <p className="mt-4 text-base font-medium text-muted-foreground tracking-wide">
          {firmName}
        </p>
        <p className="mt-2 text-xs text-muted-foreground/70 italic">
          Your AI accountant in your pocket.
        </p>
      </div>

      {/* Three circle shortcuts - staggered "thinking AI" pulse animation */}
      <div className="flex items-start justify-center gap-4 sm:gap-6">
        <CircleShortcut to="/client/ask"   icon={Sparkles}   topLabel={t('home_ask_label')}   subLabel={t('home_ask_sub')}  delayMs={0}   />
        <CircleShortcut to="/client/money" icon={Briefcase}  topLabel={t('home_biz_label')}   subLabel={t('home_biz_sub')}  delayMs={350} />
        <CircleShortcut to="/client/files" icon={FolderOpen} topLabel={t('home_docs_label')}  subLabel={t('home_docs_sub')} delayMs={700} />
      </div>
      <style>{`
        /* Slow, breath-like pulse: ~2s active swell + ~9s rest = 11s cycle.
           Scale-only so the halo stays perfectly concentric with the button. */
        @keyframes v4-think-pulse {
          0%   { transform: scale(1); }
          9%   { transform: scale(1.06); }
          18%  { transform: scale(1); }
          100% { transform: scale(1); }
        }
        @keyframes v4-think-halo {
          0%   { transform: scale(1);    opacity: 0; }
          6%   { transform: scale(1.18); opacity: 0.35; }
          18%  { transform: scale(1.55); opacity: 0; }
          100% { transform: scale(1);    opacity: 0; }
        }
      `}</style>

      {/* Powered-by footer - RoniDoc.ai mark with the 3 dots above the
          R animating in each time the home screen mounts. */}
      <div className="flex flex-col items-center gap-1.5 text-xs text-muted-foreground">
        <span>{t('powered_by_ronidoc')}</span>
        <AnimatedRoniDocLogo style={{ fontSize: '1.5rem' }} />
      </div>

      {/* Messages inbox sheet */}
      {showInbox && (
        <InboxSheet
          messages={inbox}
          timeline={timeline}
          filesTimeline={filesTimeline}
          onClose={() => setShowInbox(false)}
          onReadOne={readOne}
          onMarkAllRead={markAllRead}
          onSendReply={async (text, attachmentFileId) => {
            try {
              await sendMessage(text, attachmentFileId);
              qc.invalidateQueries({ queryKey: ['client-messages'] });
              qc.invalidateQueries({ queryKey: ['client-timeline'] });
              qc.invalidateQueries({ queryKey: ['client-files'] });
              return true;
            } catch (e: any) {
              toast.error(e?.response?.data?.error || 'Failed to send');
              return false;
            }
          }}
          onUploadAttachment={async (file) => {
            try {
              const f = await uploadFile(file);
              qc.invalidateQueries({ queryKey: ['client-files'] });
              return f.id;
            } catch (e: any) {
              toast.error(e?.response?.data?.error || e?.message || 'Upload failed');
              return null;
            }
          }}
          t={t}
          dir={dir}
        />
      )}
    </div>
  );
}

function InboxSheet({ messages, timeline, filesTimeline, onClose, onReadOne, onMarkAllRead, onSendReply, onUploadAttachment, t, dir }: {
  messages: InboxMsg[];
  timeline: TimelineEvent[];
  filesTimeline: FileTimelineItem[];
  onClose: () => void;
  onReadOne: (id: string) => void;
  onMarkAllRead: () => void;
  onSendReply: (text: string, attachmentFileId?: string) => Promise<boolean>;
  onUploadAttachment: (file: File) => Promise<string | null>;
  t: (k: string) => string;
  dir: 'ltr' | 'rtl';
}) {
  const ICON: Record<InboxMsg['type'], any> = {
    personal: MessageSquareText, update: FileCheck2, news: Newspaper, voice: Mic,
  };
  const typeLabel: Record<InboxMsg['type'], string> = {
    personal: t('type_personal'), update: t('type_update'), news: t('type_news'), voice: t('type_voice'),
  };
  const [playing, setPlaying] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replied, setReplied] = useState(false);
  const [pendingAttachment, setPendingAttachment] = useState<{ name: string; fileId: string } | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const replyFileInputRef = useRef<HTMLInputElement | null>(null);
  const selected = messages.find(m => m.id === selectedId) || null;
  const [tab, setTab] = useState<'messages' | 'timeline'>('messages');

  const qc = useQueryClient();

  // Timeline events - sourced from API
  type TLEvent = { id: string; icon: 'file' | 'message' | 'deadline' | 'done' | 'voice'; title: string; detail: string; time: string };
  const TIMELINE: TLEvent[] = timeline.map(e => ({
    id: e.id,
    icon: (e.icon as TLEvent['icon']) || (
      e.eventType === 'document_uploaded' || e.eventType === 'file_shared' ? 'file' :
      e.eventType === 'message_sent' || e.eventType === 'message_received' ? 'message' :
      e.eventType === 'deadline' ? 'deadline' :
      e.eventType === 'voice_note' ? 'voice' :
      'done'
    ),
    title: e.title,
    detail: e.description || '',
    time: relativeTime(e.occurredAt),
  }));

  // Fake "playback" progress for the voice player UX. In production this
  // would drive a real <audio> element.
  useEffect(() => {
    if (!playing) return;
    const msg = messages.find(m => m.id === playing);
    if (!msg?.voiceSec) return;
    setProgress(0);
    const total = msg.voiceSec * 1000;
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / total);
      setProgress(p);
      if (p < 1) raf = requestAnimationFrame(tick);
      else setPlaying(null);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, messages]);
  const hasUnread = messages.some(m => m.unread);

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 z-40 bg-foreground/40 backdrop-blur-sm animate-in fade-in duration-200" />
      <div className="fixed inset-x-0 bottom-0 top-12 z-50 mx-auto flex max-w-[720px] flex-col overflow-hidden rounded-t-2xl border-t border-border bg-background shadow-2xl animate-in slide-in-from-bottom duration-300">
        {/* Header - list view OR detail view */}
        {!selected ? (
          <>
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <InboxIcon className="h-4 w-4 text-[var(--v4-accent-text)]" strokeWidth={1.75} />
              <h2 className="text-sm font-bold">Inbox</h2>
            </div>
            <div className="flex items-center gap-2">
              {tab === 'messages' && hasUnread && (
                <button
                  onClick={onMarkAllRead}
                  className="text-[11px] font-semibold text-[var(--v4-accent-text)] hover:underline"
                >
                  {t('mark_all_read')}
                </button>
              )}
              <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          {/* Tabs row - Messages + Timeline. No separate Chat (Ask AI page covers that). */}
          <div className="flex border-b border-border bg-background">
            {([
              { id: 'messages', label: 'Messages', icon: Bell },
              { id: 'timeline', label: 'Timeline', icon: CalendarClock },
            ] as const).map(opt => {
              const I = opt.icon;
              const active = tab === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setTab(opt.id)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-1.5 border-b-2 px-2 py-2.5 text-xs font-semibold transition-colors',
                    active ? 'border-[var(--v4-accent)] text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground/80'
                  )}
                >
                  <I className="h-3.5 w-3.5" strokeWidth={1.75} />
                  {opt.label}
                </button>
              );
            })}
          </div>
          </>
        ) : (
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <button
              onClick={() => setSelectedId(null)}
              className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className={cn('h-4 w-4', dir === 'rtl' && 'rotate-180')} />
              {t('messages_title')}
            </button>
            <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Detail view */}
        {selected && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-5 py-5">
              <div className="flex items-center gap-2 mb-2">
                <div className={cn(
                  'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md',
                  selected.type === 'personal' && 'bg-[var(--v4-accent)] text-white',
                  selected.type === 'voice'    && 'bg-[var(--v4-accent)] text-white',
                  selected.type === 'update'   && 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400',
                  selected.type === 'news'     && 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
                )}>
                  {(() => { const I = ICON[selected.type]; return <I className="h-4 w-4" strokeWidth={1.75} />; })()}
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{typeLabel[selected.type]}</p>
                  <p className="text-[10px] text-muted-foreground/70">{selected.time}</p>
                </div>
              </div>
              <h2 dir={dirOf(selected.title)} style={{ textAlign: dirOf(selected.title) === 'rtl' ? 'right' : 'left' }} className="text-xl font-bold leading-tight text-foreground mt-3">{selected.title}</h2>

              {selected.type === 'voice' && selected.voiceSec ? (
                <div className="mt-4 flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                  <button
                    onClick={() => setPlaying(playing === selected.id ? null : selected.id)}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white hover:bg-[var(--v4-accent-hover)]"
                  >
                    {playing === selected.id ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ms-0.5" />}
                  </button>
                  <Waveform progress={playing === selected.id ? progress : 0} />
                  <span className="text-xs font-mono tabular-nums text-muted-foreground">
                    {`${Math.floor(selected.voiceSec/60)}:${String(selected.voiceSec%60).padStart(2,'0')}`}
                  </span>
                </div>
              ) : null}

              {/* Attachment (if any) */}
              {selected.attachmentUrl && selected.attachmentContentType?.startsWith('image/') && (
                <a href={selected.attachmentUrl} target="_blank" rel="noopener noreferrer" className="mt-4 block">
                  <img src={selected.attachmentUrl} alt={selected.attachmentName || ''}
                    className="w-full rounded-lg border border-border max-h-96 object-cover" />
                </a>
              )}
              {selected.attachmentUrl && selected.attachmentContentType?.startsWith('audio/') && (
                <audio src={selected.attachmentUrl} controls className="mt-4 w-full" />
              )}
              {selected.attachmentUrl && !selected.attachmentContentType?.startsWith('image/') && !selected.attachmentContentType?.startsWith('audio/') && (
                <a href={selected.attachmentUrl} target="_blank" rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-medium hover:bg-muted/50">
                  <FileText className="h-4 w-4" />
                  <span className="truncate">{selected.attachmentName || 'attachment'}</span>
                </a>
              )}

              {selected.preview && (
                <p dir={dirOf(selected.preview)} style={{ textAlign: dirOf(selected.preview) === 'rtl' ? 'right' : 'left' }} className="mt-4 text-sm leading-relaxed text-foreground/90">{selected.preview}</p>
              )}

              {/* Reply composer */}
              <div className="mt-6 border-t border-border pt-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">Reply</p>
                {replied ? (
                  <p className="rounded-md border border-[var(--v4-accent)]/40 bg-[var(--v4-accent-tint)] p-3 text-sm text-foreground">
                    {t('message_sent')}
                  </p>
                ) : voiceMode ? (
                  <VoiceRecorder
                    onSent={() => {
                      qc.invalidateQueries({ queryKey: ['client-messages'] });
                      qc.invalidateQueries({ queryKey: ['client-timeline'] });
                      setVoiceMode(false);
                      setReplied(true);
                    }}
                    onCancel={() => setVoiceMode(false)}
                  />
                ) : (
                  <>
                    <textarea
                      value={replyText}
                      onChange={e => setReplyText(e.target.value)}
                      placeholder={t('type_your_message')}
                      rows={3}
                      className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:border-[var(--v4-accent)] focus:outline-none resize-none"
                    />
                    {pendingAttachment && (
                      <div className="mt-2 flex items-center gap-2 rounded-md border border-[var(--v4-accent)]/40 bg-[var(--v4-accent-tint)] px-2.5 py-1.5">
                        <Paperclip className="h-3.5 w-3.5 text-[var(--v4-accent-text)]" />
                        <span className="text-xs font-medium truncate flex-1">{pendingAttachment.name}</span>
                        <button onClick={() => setPendingAttachment(null)} className="text-muted-foreground hover:text-foreground">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                    <input ref={replyFileInputRef} type="file"
                      accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (!file) return;
                        setUploadingAttachment(true);
                        const fileId = await onUploadAttachment(file);
                        setUploadingAttachment(false);
                        if (fileId) setPendingAttachment({ name: file.name, fileId });
                      }}
                      className="hidden" />
                    <div className="mt-2 flex gap-2">
                      <button
                        onClick={async () => {
                          const text = replyText.trim();
                          if (!text && !pendingAttachment) return;
                          const ok = await onSendReply(text, pendingAttachment?.fileId);
                          if (ok) { setReplied(true); setReplyText(''); setPendingAttachment(null); }
                        }}
                        disabled={(!replyText.trim() && !pendingAttachment) || uploadingAttachment}
                        className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--v4-accent)] py-2.5 text-sm font-semibold text-white hover:bg-[var(--v4-accent-hover)] disabled:bg-muted disabled:text-muted-foreground"
                      >
                        <Send className="h-4 w-4" />
                        {t('send_now')}
                      </button>
                      <button
                        onClick={() => replyFileInputRef.current?.click()}
                        disabled={uploadingAttachment}
                        className="flex items-center justify-center gap-2 rounded-md border border-border bg-card px-3 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-50"
                        aria-label="Attach file"
                      >
                        {uploadingAttachment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => setVoiceMode(true)}
                        className="flex items-center justify-center gap-2 rounded-md border border-border bg-card px-3 text-sm font-semibold text-foreground hover:bg-muted"
                        aria-label="Record voice message"
                      >
                        <Mic className="h-4 w-4" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Messages tab - continuous chat thread (WhatsApp style) */}
        {!selected && tab === 'messages' && (
          <ChatThread
            messages={messages}
            onSendReply={onSendReply}
            onUploadAttachment={onUploadAttachment}
            onMarkAllRead={onMarkAllRead}
            t={t}
          />
        )}

        {/* Timeline tab - file send/receive history, centre line with
            sent on one side and received on the other. */}
        {!selected && tab === 'timeline' && (
          <FilesTimeline items={filesTimeline} t={t} />
        )}

      </div>
    </>
  );
}

/** Friendly "Sent a voice message" / "Received an image" phrase - never the
 *  raw filename. */
const GENERIC_TITLES = new Set(['Document', 'File', 'Photo', 'Image', 'Voice message']);
function filePhrase(item: FileTimelineItem): string {
  const verb = item.direction === 'sent' ? 'Sent' : 'Received';
  const m = (item.contentType || '').toLowerCase();
  let noun: string;
  if (m.startsWith('audio/')) noun = 'a voice message';
  else if (m.startsWith('image/')) noun = 'an image';
  else if (m === 'application/pdf' && GENERIC_TITLES.has(item.title)) noun = 'a PDF file';
  else if (!GENERIC_TITLES.has(item.title)) noun = `a ${item.title.toLowerCase()}`; // "a receipt", "a bank statement"
  else noun = 'a file';
  return `${verb} ${noun}`;
}

/**
 * Two-sided file history. A line runs down the centre; files the client SENT
 * sit on the right (green), files RECEIVED from the firm sit on the left
 * (blue). Each card is a friendly phrase that links to the file - no raw
 * filenames.
 */
function FilesTimeline({ items, t }: { items: FileTimelineItem[]; t: (k: string) => string }) {
  const [opening, setOpening] = useState<string | null>(null);

  const openFile = async (item: FileTimelineItem) => {
    if (opening) return;
    // Open the tab SYNCHRONOUSLY (inside the click gesture) so popup blockers
    // don't reject it, then navigate it once the signed URL resolves.
    const win = window.open('', '_blank');
    setOpening(item.id);
    try {
      const { url } = await getFileDownloadUrl(item.id);
      if (win) win.location.href = url;
      else window.location.href = url; // popup blocked anyway - fall back to same tab
    } catch (e: any) {
      if (win) win.close();
      toast.error(e?.response?.data?.error || 'Could not open file');
    } finally {
      setOpening(null);
    }
  };

  if (!items.length) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground gap-2 px-6">
        <FileText className="h-10 w-10 opacity-40" />
        <p className="text-sm text-center">No files yet. Files you send and receive will appear here.</p>
      </div>
    );
  }

  const fileIcon = (ct?: string | null) => {
    const m = (ct || '').toLowerCase();
    if (m.startsWith('audio/')) return Mic;
    if (m.startsWith('image/')) return ImageIcon;
    return FileText;
  };
  const actionHint = (ct?: string | null) => {
    const m = (ct || '').toLowerCase();
    if (m.startsWith('audio/')) return 'Tap to play';
    return 'Tap to open';
  };
  const fmtDateTime = (iso: string) =>
    new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  return (
    <div className="flex-1 overflow-y-auto px-3 py-4">
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mb-4 text-[11px]">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-sky-500" /> Received
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Sent
        </span>
      </div>

      <div className="relative">
        {/* Centre line */}
        <span className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-border" />
        <div className="space-y-4">
          {items.map(item => {
            const sent = item.direction === 'sent';
            const Icon = fileIcon(item.contentType);
            const isOpening = opening === item.id;
            const accent = sent
              ? 'border-emerald-300 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/30'
              : 'border-sky-300 dark:border-sky-900/60 bg-sky-50 dark:bg-sky-950/30';
            const dot = sent ? 'bg-emerald-500' : 'bg-sky-500';
            const iconCls = sent ? 'text-emerald-600 dark:text-emerald-300' : 'text-sky-600 dark:text-sky-300';
            const card = (
              <button
                onClick={() => openFile(item)}
                disabled={isOpening}
                className={cn('max-w-[94%] w-fit rounded-xl border p-2.5 text-left transition-colors hover:brightness-95 active:brightness-90 disabled:opacity-70', accent, sent ? '' : 'text-right')}
              >
                <div className={cn('flex items-center gap-1.5', !sent && 'justify-end')}>
                  {sent && <Icon className={cn('h-3.5 w-3.5 shrink-0', iconCls)} />}
                  <span className="text-sm font-semibold leading-tight">{filePhrase(item)}</span>
                  {!sent && <Icon className={cn('h-3.5 w-3.5 shrink-0', iconCls)} />}
                </div>
                <p className={cn('text-[11px] mt-0.5 underline decoration-dotted', iconCls)}>
                  {isOpening ? 'Opening…' : actionHint(item.contentType)}
                </p>
                {item.senderName && (
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {sent ? 'by ' : 'from '}{item.senderName}
                  </p>
                )}
                <p className="text-[10px] text-muted-foreground/70 mt-0.5">{fmtDateTime(item.occurredAt)}</p>
              </button>
            );
            return (
              <div key={item.id} className="relative flex items-stretch">
                <div className="w-1/2 pr-4 flex justify-end">{!sent && card}</div>
                <span className={cn('absolute left-1/2 -translate-x-1/2 top-3 h-3 w-3 rounded-full ring-4 ring-background', dot)} />
                <div className="w-1/2 pl-4 flex justify-start">{sent && card}</div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="sr-only">{t('timeline')}</p>
    </div>
  );
}

/**
 * Continuous chat thread rendered WhatsApp-style.
 * - Messages sorted chronologically (oldest first, newest at bottom)
 * - Day separators between messages on different days
 * - Bubble alignment: 'outbound' (firm) on left, 'inbound' (you) on right
 * - Sticky composer pinned at the bottom (text + attach + voice + send)
 * - Auto-scrolls to bottom on mount + when new messages arrive
 * - Marks all read once when the thread first renders with unread messages
 */
function ChatThread({ messages, onSendReply, onUploadAttachment, onMarkAllRead, t }: {
  messages: InboxMsg[];
  onSendReply: (text: string, attachmentFileId?: string) => Promise<boolean>;
  onUploadAttachment: (file: File) => Promise<string | null>;
  onMarkAllRead: () => void;
  t: (k: string) => string;
}) {
  const sorted = [...messages].sort((a, b) => +new Date(a.createdAt) - +new Date(b.createdAt));
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [text, setText] = useState('');
  const [pending, setPending] = useState<{ name: string; fileId: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [voiceMode, setVoiceMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const markedReadRef = useRef(false);

  // Mark all as read once on first render when unread present
  useEffect(() => {
    if (markedReadRef.current) return;
    if (sorted.some(m => m.unread)) {
      markedReadRef.current = true;
      onMarkAllRead();
    }
  }, [sorted, onMarkAllRead]);

  // Scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [sorted.length]);

  async function send() {
    const value = text.trim();
    if (!value && !pending) return;
    if (sending) return;
    setSending(true);
    const ok = await onSendReply(value, pending?.fileId);
    setSending(false);
    if (ok) { setText(''); setPending(null); }
  }

  if (sorted.length === 0) {
    return (
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
          <div>
            <MessageCircle className="mx-auto h-10 w-10 opacity-40 mb-2" />
            <p>{t('messages_empty')}</p>
            <p className="text-xs mt-2">Type below to send your accountant a message.</p>
          </div>
        </div>
        <ChatComposer
          text={text} setText={setText}
          pending={pending} setPending={setPending}
          uploading={uploading} setUploading={setUploading}
          sending={sending}
          voiceMode={voiceMode} setVoiceMode={setVoiceMode}
          onSend={send}
          onUploadAttachment={onUploadAttachment}
          fileInputRef={fileInputRef}
          t={t}
        />
      </div>
    );
  }

  // Group by day for separator labels
  let lastDay = '';
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
        {sorted.map(m => {
          const day = dayLabel(m.createdAt);
          const showSep = day !== lastDay;
          lastDay = day;
          return (
            <Fragment key={m.id}>
              {showSep && (
                <div className="flex items-center justify-center my-3">
                  <span className="rounded-full bg-muted/80 text-muted-foreground px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{day}</span>
                </div>
              )}
              <ChatBubble m={m} />
            </Fragment>
          );
        })}
      </div>
      {voiceMode ? (
        <div className="border-t border-border px-3 py-2 bg-card">
          <VoiceRecorder onSent={() => setVoiceMode(false)} onCancel={() => setVoiceMode(false)} />
        </div>
      ) : (
        <ChatComposer
          text={text} setText={setText}
          pending={pending} setPending={setPending}
          uploading={uploading} setUploading={setUploading}
          sending={sending}
          voiceMode={voiceMode} setVoiceMode={setVoiceMode}
          onSend={send}
          onUploadAttachment={onUploadAttachment}
          fileInputRef={fileInputRef}
          t={t}
        />
      )}
    </div>
  );
}

// Persian / Arabic block - if the message contains ANY RTL letter we render
// the bubble RTL, rather than relying on dir="auto" (which keys off only the
// first strong character and trips up when a message opens with a name,
// bullet, or Latin word).
const RTL_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/;
function dirOf(text?: string | null): 'rtl' | 'ltr' {
  return text && RTL_RE.test(text) ? 'rtl' : 'ltr';
}

function ChatBubble({ m }: { m: InboxMsg }) {
  const fromFirm = m.direction === 'outbound';
  const contentDir = dirOf(m.preview);
  return (
    <div className={cn('flex', fromFirm ? 'justify-start' : 'justify-end')}>
      <div className={cn('flex flex-col gap-0.5 max-w-[82%]', fromFirm ? 'items-start' : 'items-end')}>
        {fromFirm && m.senderName && (
          <span className="text-[10px] text-muted-foreground px-1">{m.senderName}</span>
        )}
        {m.attachmentUrl && m.attachmentContentType?.startsWith('image/') && (
          <a href={m.attachmentUrl} target="_blank" rel="noopener noreferrer">
            <img src={m.attachmentUrl} alt={m.attachmentName || ''} className="rounded-2xl border border-border max-h-72 object-cover" />
          </a>
        )}
        {m.attachmentUrl && m.attachmentContentType?.startsWith('audio/') && (
          <audio src={m.attachmentUrl} controls className="max-w-full" />
        )}
        {m.attachmentFileId && !m.attachmentContentType?.startsWith('image/') && !m.attachmentContentType?.startsWith('audio/') && (
          <a href={m.attachmentUrl || '#'} target="_blank" rel="noopener noreferrer"
             className={cn(
               'inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm max-w-full',
               fromFirm ? 'bg-card border-border' : 'bg-[var(--v4-accent)] text-white border-transparent',
             )}>
            <FileText className="h-4 w-4 shrink-0" />
            <span className="truncate flex-1">{m.attachmentName || 'attachment'}</span>
          </a>
        )}
        {m.preview && (
          <div
            dir={contentDir}
            style={{ textAlign: contentDir === 'rtl' ? 'right' : 'left' }}
            className={cn(
              'rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-sm',
              fromFirm ? 'bg-card border border-border text-foreground rounded-bl-md' : 'bg-[var(--v4-accent)] text-white rounded-br-md',
            )}
          >
            {m.preview}
          </div>
        )}
        <span className="text-[10px] text-muted-foreground px-1">{timeOfDay(m.createdAt)}</span>
      </div>
    </div>
  );
}

function ChatComposer({ text, setText, pending, setPending, uploading, setUploading, sending, voiceMode, setVoiceMode, onSend, onUploadAttachment, fileInputRef, t }: any) {
  return (
    <div className="border-t border-border px-3 py-2 bg-card">
      {pending && (
        <div className="mb-2 flex items-center gap-2 rounded-md border border-[var(--v4-accent)]/40 bg-[var(--v4-accent-tint)] px-2.5 py-1.5">
          <Paperclip className="h-3.5 w-3.5 text-[var(--v4-accent-text)]" />
          <span className="text-xs font-medium truncate flex-1">{pending.name}</span>
          <button onClick={() => setPending(null)} className="text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      <input ref={fileInputRef} type="file"
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
        onChange={async (e: any) => {
          const file = e.target.files?.[0]; e.target.value = '';
          if (!file) return;
          setUploading(true);
          const fileId = await onUploadAttachment(file);
          setUploading(false);
          if (fileId) setPending({ name: file.name, fileId });
        }}
        className="hidden" />
      <div className="flex items-end gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-muted-foreground hover:text-foreground disabled:opacity-50"
          aria-label="Attach"
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </button>
        <textarea
          value={text}
          onChange={(e: any) => setText(e.target.value)}
          onKeyDown={(e: any) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
          }}
          placeholder={t('type_your_message')}
          rows={1}
          className="flex-1 resize-none rounded-2xl border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:border-[var(--v4-accent)] focus:outline-none max-h-32"
        />
        {(text.trim() || pending) ? (
          <button
            onClick={onSend}
            disabled={sending}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white shadow-sm disabled:opacity-50"
            aria-label="Send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        ) : (
          <button
            onClick={() => setVoiceMode(true)}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--v4-accent)] text-white shadow-sm"
            aria-label="Voice message"
          >
            <Mic className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

// 20-bar waveform - bars filled to the left of the playback head.
const WAVE_BARS = [4, 7, 11, 8, 14, 10, 6, 13, 9, 12, 7, 10, 14, 8, 5, 11, 9, 13, 7, 4];
function Waveform({ progress }: { progress: number }) {
  const cutoff = Math.floor(progress * WAVE_BARS.length);
  return (
    <div className="flex flex-1 items-center gap-[2px] h-5">
      {WAVE_BARS.map((h, i) => (
        <span
          key={i}
          className={cn(
            'flex-1 rounded-full transition-colors',
            i < cutoff ? 'bg-[var(--v4-accent)]' : 'bg-muted-foreground/30'
          )}
          style={{ height: `${h * 1.2}px` }}
        />
      ))}
    </div>
  );
}

function CircleShortcut({ to, icon: Icon, topLabel, subLabel, delayMs }: {
  to: string; icon: any; topLabel: string; subLabel: string; delayMs: number;
}) {
  return (
    <div className="relative">
      {/* Halo ripple - same delay so it pulses with the dot */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full bg-[var(--v4-accent)]"
        style={{
          animation: 'v4-think-halo 7s ease-in-out infinite',
          animationDelay: `${delayMs}ms`,
        }}
      />
      <Link
        to={to}
        className={cn(
          'relative group flex h-[112px] w-[112px] sm:h-[128px] sm:w-[128px] flex-col items-center justify-center rounded-full',
          'bg-[var(--v4-accent)] text-white shadow-md',
          'hover:bg-[var(--v4-accent-hover)] hover:shadow-lg',
          'active:scale-95 transition-colors'
        )}
        style={{
          animation: 'v4-think-pulse 7s ease-in-out infinite',
          animationDelay: `${delayMs}ms`,
        }}
      >
        <Icon className="h-7 w-7 mb-1 opacity-90" strokeWidth={1.25} />
        <span className="text-xl font-semibold leading-none">{topLabel}</span>
        <span className="mt-1 text-[10px] font-medium uppercase tracking-wider opacity-80">{subLabel}</span>
      </Link>
    </div>
  );
}
